/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import { View } from 'backbone.marionette';

import lazyLoadView from '@/util/lazyLoadView';

import IntDashboardSidebarNavView from '../../../internal-dashboard/views/IntDashboardSidebarNavView';
import SearchCustomerAssessments from '../../services/SearchCustomerAssessments';
import GetAllRegions from '../../../common/services/GetAllRegions';
import GetInternalAssessmentStatuses from '../../services/GetInternalAssessmentStatuses';
import GetExternalAssessmentStatuses from '../../services/GetExternalAssessmentStatuses';
import GetAllAssessmentTypes from '../../services/GetAllAssessmentTypes';
import loadSearchFields from '../../../login/loaders/SearchFieldsViewLoader';
import AssessmentDashboardGridView from '../../views/AssessmentDashboardGridView';

import tmplAssessmentDashboardPage from './AssessmentDashboardPage.hbs';

const AssessmentDashboardPage = View.extend({
  template: tmplAssessmentDashboardPage,

  ui: {
    sideBar: '.sidebar-nav-container',
    searchFieldsView: '.search-fields-view',
    assessmentsGrid: '#assessmentsGrid'
  },

  regions: {
    sideBar: '@ui.sideBar',
    searchFieldsView: '@ui.searchFieldsView',
    assessmentsGrid: '@ui.assessmentsGrid'
  },

  childViewEvents: {
    'search:response': 'onSearchResponse'
  },

  onSearchResponse(response) {
    this.$('.result-count').empty().append(`<span><b>Total Result Count: ${response.totalCount}</b></span>`);
    this.assessmentsGrid.collection.fullCollection.reset(response.assessments);
    this.assessmentsGrid.getViewGrid().sort('submittedOn', 'descending');
  },

  onRender() {
    const isInternalUser = !EXTERNAL;
    const whichLookupService = isInternalUser ? GetInternalAssessmentStatuses : GetExternalAssessmentStatuses;
    const whichStatusName = isInternalUser ? 'statusID' : 'statusGroupID';

    if (isInternalUser) {
      this.showChildView('sideBar', new IntDashboardSidebarNavView({ mode: 'assessments' }));
    }

    const searchFields = [
      {
        id: 'id-search-field',
        name: 'customerAssessmentID',
        class: 'InputSearchField',
        placeholder: 'Assessment ID',
        dataParsleyType: 'integer'
      },
      {
        id: 'type-id-search-field',
        name: 'typeID',
        class: 'SelectSearchField',
        placeholder: 'Type',
        lookupService: GetAllAssessmentTypes
      },
      {
        id: 'status-id-search-field',
        name: whichStatusName,
        class: 'SelectSearchField',
        placeholder: 'Status',
        lookupService: whichLookupService,
        lookupMapping: (status) => {
          if (isInternalUser) {
            const { internalAssessmentStatus, id } = status;
            return { optionText: internalAssessmentStatus, id };
          } else {
            const { externalAssessmentStatus, id } = status;
            return { optionText: externalAssessmentStatus, id };
          }
        },
        lookupFilter: (status) => {
          if (isInternalUser) {
            return status.internalAssessmentStatus !== null;
          } else {
            return status;
          }
        }
      },
      {
        id: 'customer-name-search-field',
        name: 'customerName',
        class: 'InputSearchField',
        placeholder: 'Customer Name',
        renderable: !EXTERNAL
      },
      {
        id: 'region-id-search-field',
        name: 'regionID',
        class: 'SelectSearchField',
        placeholder: 'Location',
        lookupService: GetAllRegions,
        renderable: !EXTERNAL
      },
      {
        id: 'crs-assigned-search-field',
        name: 'crsName',
        class: 'InputSearchField',
        placeholder: 'CRS Assigned',
        renderable: !EXTERNAL
      },
      {
        id: 'command-name-search-field',
        name: 'commandName',
        class: 'InputSearchField',
        placeholder: 'Command Name',
        renderable: !EXTERNAL
      }
    ];

    this.showChildView(
      'searchFieldsView',
      lazyLoadView(() => loadSearchFields(searchFields, SearchCustomerAssessments))
    );

    this.assessmentsGrid = new AssessmentDashboardGridView({});

    this.showChildView('assessmentsGrid', this.assessmentsGrid);
  }
});

export default AssessmentDashboardPage;
